import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Monitor, 
  Smartphone, 
  Tablet,
  RefreshCw,
  Code,
  Eye,
  Loader2
} from 'lucide-react';
import {
  EmailTemplateConfig,
  generatePreviewHtml,
} from '@/services/emailTemplateConfigService';

interface TemplatePreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: EmailTemplateConfig | null;
}

type DeviceType = 'desktop' | 'tablet' | 'mobile';

const deviceWidths: Record<DeviceType, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
};

const deviceIcons = {
  desktop: Monitor,
  tablet: Tablet,
  mobile: Smartphone,
};

export function TemplatePreviewModal({ 
  open, 
  onOpenChange, 
  template 
}: TemplatePreviewModalProps) {
  const [device, setDevice] = useState<DeviceType>('desktop');
  const [previewData, setPreviewData] = useState<string>('');
  const [renderedHtml, setRenderedHtml] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'preview' | 'data'>('preview');

  // Initialize preview data when template changes
  useEffect(() => {
    if (template?.sample_data) {
      const sampleData = template.sample_data as Record<string, any>;
      setPreviewData(JSON.stringify(sampleData, null, 2));
      loadPreview(template, sampleData);
    }
  }, [template?.id]);

  const loadPreview = async (
    templateToPreview: EmailTemplateConfig, 
    data: Record<string, any>
  ) => {
    setIsLoading(true);
    try {
      const html = await generatePreviewHtml(
        templateToPreview.template_key,
        data
      );
      setRenderedHtml(html);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshPreview = async () => {
    if (!template) return;

    try {
      const parsedData = JSON.parse(previewData);
      await loadPreview(template, parsedData);
      toast.success('Preview refreshed');
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        toast.error('Failed to refresh preview');
      }
    }
  };

  const handleResetData = () => {
    if (template?.sample_data) {
      const sampleData = template.sample_data as Record<string, any>;
      setPreviewData(JSON.stringify(sampleData, null, 2));
      loadPreview(template, sampleData);
      toast.success('Data reset to defaults');
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl">{template.template_name}</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {template.description}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{template.category}</Badge>
              <Badge variant="outline">{template.template_key}</Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Main Preview Area */}
          <div className="flex-1 flex flex-col">
            {/* Device Toolbar */}
            <div className="flex items-center justify-between px-6 py-3 border-b bg-muted/30">
              <div className="flex items-center gap-2">
                {(['desktop', 'tablet', 'mobile'] as DeviceType[]).map((deviceType) => {
                  const Icon = deviceIcons[deviceType];
                  return (
                    <Button
                      key={deviceType}
                      variant={device === deviceType ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDevice(deviceType)}
                      className="gap-2"
                    >
                      <Icon className="h-4 w-4" />
                      {deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}
                    </Button>
                  );
                })}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshPreview}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Preview Content */}
            <ScrollArea className="flex-1 bg-muted/20">
              <div className="flex justify-center p-6">
                <div
                  style={{ 
                    width: deviceWidths[device],
                    maxWidth: '100%',
                    transition: 'width 0.3s ease'
                  }}
                  className="bg-background rounded-lg shadow-xl overflow-hidden"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center h-96">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : (
                    <iframe
                      srcDoc={renderedHtml}
                      className="w-full border-0"
                      style={{ 
                        height: device === 'mobile' ? '667px' : '800px',
                        minHeight: '400px'
                      }}
                      title="Email Preview"
                      sandbox="allow-same-origin"
                    />
                  )}
                </div>
              </div>
            </ScrollArea>
          </div>

          {/* Side Panel */}
          <div className="w-96 border-l flex flex-col">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'preview' | 'data')}>
              <TabsList className="w-full rounded-none border-b">
                <TabsTrigger value="preview" className="flex-1">
                  <Eye className="h-4 w-4 mr-2" />
                  Info
                </TabsTrigger>
                <TabsTrigger value="data" className="flex-1">
                  <Code className="h-4 w-4 mr-2" />
                  Data
                </TabsTrigger>
              </TabsList>

              <TabsContent value="preview" className="flex-1 p-4 space-y-4 mt-0">
                <ScrollArea className="h-full">
                  <div className="space-y-4">
                    {/* Template Info */}
                    <div>
                      <h3 className="font-semibold mb-2">Template Information</h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">From:</span>
                          <p className="font-medium mt-1">
                            {template.from_name} &lt;{template.from_email}&gt;
                          </p>
                        </div>
                        {template.reply_to && (
                          <div>
                            <span className="text-muted-foreground">Reply To:</span>
                            <p className="font-medium mt-1">{template.reply_to}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Variables */}
                    <div>
                      <h3 className="font-semibold mb-2">Available Variables</h3>
                      <div className="flex flex-wrap gap-1">
                        {(template.variables as string[]).map((variable) => (
                          <Badge key={variable} variant="outline" className="text-xs">
                            {variable}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Required Variables */}
                    {(template.required_variables as string[]).length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-2">Required Variables</h3>
                        <div className="flex flex-wrap gap-1">
                          {(template.required_variables as string[]).map((variable) => (
                            <Badge 
                              key={variable} 
                              variant="default" 
                              className="text-xs"
                            >
                              {variable}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Status */}
                    <div>
                      <h3 className="font-semibold mb-2">Status</h3>
                      <Badge variant={template.enabled ? 'default' : 'secondary'}>
                        {template.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="data" className="flex-1 flex flex-col p-4 space-y-2 mt-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Preview Data (JSON)</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetData}
                  >
                    Reset
                  </Button>
                </div>
                <Textarea
                  value={previewData}
                  onChange={(e) => setPreviewData(e.target.value)}
                  className="flex-1 font-mono text-xs resize-none"
                  placeholder="Edit JSON data..."
                />
                <Button 
                  onClick={handleRefreshPreview}
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Rendering...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Apply & Refresh
                    </>
                  )}
                </Button>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
